import { useState, useRef } from "react";
import { Link } from "react-router-dom";
import { Dog, Cat, AlertTriangle, ChevronRight, Clock, MapPin, Users, Heart, ChevronLeft, MapPinOff, Phone, Mail, BadgeCheck, HelpCircle, ArrowUp, ArrowRight } from "lucide-react";
import { trackEvent } from "@/hooks/useEventTracking";
import { shelterFields } from "@/lib/analyticsEvents";
import type { ShelterWithDistance } from "@/pages/Index";
import { isUrgentNeedsActive } from "@/types/shelter";
import { useIsTabletOrMobile } from "@/hooks/use-mobile";
import { formatDistanceToNow } from "date-fns";
import { pl } from "date-fns/locale";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { ShareChoiceDialog } from "./ShareChoiceDialog";
import { VerificationBadge } from "./VerificationBadge";
import { UrgentBadge } from "./UrgentBadge";
import { formatPhoneDisplay } from "@/lib/phoneFormat";
import { ShelterCardSections } from "./ShelterCardSections";
import type { ShelterSectionsMap } from "@/types/shelterSections";
import { getSectionsFor } from "@/hooks/useShelterSections";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { EmptyState } from "./EmptyState";
import { shelterUrl } from "@/lib/shelterSlug";

interface ShelterListProps {
  shelters: ShelterWithDistance[];
  selectedShelterId: string | null;
  onShelterSelect: (shelter: ShelterWithDistance) => void;
  containerRef?: React.RefObject<HTMLDivElement>;
  onClearFilters?: () => void;
  hasActiveFilters?: boolean;
  searchQuery?: string;
  filterCity?: string;
  allShelters?: ShelterWithDistance[];
  onSuggestionClick?: (q: string) => void;
  suggestionsEnabled?: boolean;
  sectionsMap?: ShelterSectionsMap;
}

const ITEMS_PER_PAGE = 4;

// Check if shelter has a proper address (not just city name)
const hasExactLocation = (shelter: ShelterWithDistance): boolean => {
  if (!shelter.address) return false;
  const addressLower = shelter.address.toLowerCase().trim();
  const cityLower = shelter.city.toLowerCase().trim();
  if (addressLower === '' || addressLower === cityLower) return false;
  if (addressLower === `${cityLower}, polska` || addressLower === `${cityLower}, poland`) return false;
  return true;
};

export const ShelterList = ({ shelters, selectedShelterId, onShelterSelect, containerRef, onClearFilters, hasActiveFilters, searchQuery, filterCity, allShelters, onSuggestionClick, suggestionsEnabled, sectionsMap }: ShelterListProps) => {
  const [currentPage, setCurrentPage] = useState(1);
  const isTabletOrMobile = useIsTabletOrMobile();
  const listRef = useRef<HTMLDivElement>(null);

  const scrollToTop = () => {
    containerRef?.current?.scrollTo({ top: 0, behavior: 'smooth' });
  };
  
  const totalPages = Math.ceil(shelters.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  
  // On desktop show all shelters, on mobile/tablet use pagination
  const displayedShelters = isTabletOrMobile 
    ? shelters.slice(startIndex, startIndex + ITEMS_PER_PAGE)
    : shelters;

  // Reset to page 1 when shelters change (e.g., after filtering)
  if (currentPage > totalPages && totalPages > 0) {
    setCurrentPage(1);
  }

  if (shelters.length === 0) {
    return (
      <EmptyState 
        type="no-results" 
        onClearFilters={onClearFilters}
        hasActiveFilters={hasActiveFilters}
        searchQuery={searchQuery}
        city={filterCity}
        allShelters={allShelters}
        onSuggestionClick={onSuggestionClick}
        suggestionsEnabled={suggestionsEnabled}
      />
    );
  }

  const getLastUpdatedText = (dateString: string) => {
    try {
      return formatDistanceToNow(new Date(dateString), { addSuffix: true, locale: pl });
    } catch {
      return null;
    }
  };

  const formatDistance = (distance: number): string => {
    if (distance < 1) {
      return `${Math.round(distance * 1000)} m`;
    }
    return `${distance.toFixed(1)} km`;
  };

  return (
    <div className="space-y-2" ref={listRef}>
      {displayedShelters.map((shelter, index) => (
        <div
          key={shelter.id}
          data-shelter-id={shelter.id}
          role="button"
          tabIndex={0}
          onClick={() => {
            trackEvent("Otworzono schronisko", { ...shelterFields(shelter), source: "list" });
            onShelterSelect(shelter);
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              trackEvent("Otworzono schronisko", { ...shelterFields(shelter), source: "list" });
              onShelterSelect(shelter);
            }
          }}
          className="shelter-card group w-full text-left relative"
        >
          <div className="flex items-start justify-between gap-2">
            <div className="flex-1 min-w-0">
              <div className="relative pr-16 sm:pr-28">
                <p className="font-display font-bold text-card-foreground text-sm sm:text-base leading-tight break-words m-0 tracking-tight">
                  {shelter.name}
                  <Link
                    to={shelterUrl(shelter)}
                    onClick={(e) => e.stopPropagation()}
                    className="sr-only"
                  >
                    Profil schroniska {shelter.name}
                  </Link>
                </p>
                <div className="absolute right-0 top-0 h-[1.125rem] sm:h-5 flex items-center gap-1.5 sm:gap-2 flex-shrink-0">
                  <VerificationBadge verified={!!shelter.organization_verified} size="sm" />
                  <span className="opacity-70 group-hover:opacity-100 transition-opacity">
                    <ShareChoiceDialog shelter={shelter} variant="icon" />
                  </span>
                </div>
              </div>
              <div className="flex items-center flex-wrap gap-x-2 gap-y-0.5 mt-1 text-[10.5px] sm:text-[11px] text-muted-foreground/80 leading-tight">
                <span className="inline-flex items-center gap-1 whitespace-nowrap">
                  <MapPin className="w-2.5 h-2.5" />
                  {shelter.city}{shelter.region && `, ${shelter.region}`}
                </span>
                {shelter.email && (
                  <>
                    <span className="text-muted-foreground/40 hidden sm:inline" aria-hidden>·</span>
                    <a
                      href={`mailto:${shelter.email}`}
                      onClick={(e) => { e.stopPropagation(); trackEvent("Email", shelterFields(shelter)); }}
                      className="hidden sm:inline-flex items-center gap-1 hover:text-primary transition-colors min-w-0"
                    >
                      <Mail className="w-2.5 h-2.5 flex-shrink-0" />
                      <span className="truncate">{shelter.email}</span>
                    </a>
                  </>
                )}
                {shelter.phone && (
                  <>
                    <span className="text-muted-foreground/40 hidden sm:inline" aria-hidden>·</span>
                    <a
                      href={`tel:${shelter.phone.split(';')[0].trim()}`}
                      onClick={(e) => { e.stopPropagation(); trackEvent("Telefon", shelterFields(shelter)); }}
                      className="hidden sm:inline-flex items-center gap-1 hover:text-primary transition-colors whitespace-nowrap"
                    >
                      <Phone className="w-2.5 h-2.5 flex-shrink-0" />
                      <span>{formatPhoneDisplay(shelter.phone.split(';')[0])}</span>
                    </a>
                  </>
                )}
                {hasExactLocation(shelter) ? (
                  shelter.distance !== undefined && (
                    <span className="inline-flex items-center gap-1 text-[10px] font-medium text-primary bg-primary/10 px-1.5 py-0 leading-4 rounded-full whitespace-nowrap flex-shrink-0 ml-auto">
                      {formatDistance(shelter.distance)}
                    </span>
                  )
                ) : (
                  <span className="inline-flex items-center gap-1 text-[10px] font-medium text-muted-foreground bg-muted px-1.5 py-0 leading-4 rounded-full whitespace-nowrap flex-shrink-0 ml-auto">
                    <MapPinOff className="w-2.5 h-2.5" />
                    <span className="hidden xs:inline">Brak lokalizacji</span>
                  </span>
                )}
              </div>

              {/* Mobile-only contact row: email + phone in one line */}
              {(shelter.email || shelter.phone) && (
                <div className="flex sm:hidden items-center flex-wrap gap-x-2 gap-y-0.5 mt-1 text-[10.5px] text-muted-foreground/80 leading-tight">
                  {shelter.phone && (
                    <a
                      href={`tel:${shelter.phone.split(';')[0].trim()}`}
                      onClick={(e) => { e.stopPropagation(); trackEvent("Telefon", shelterFields(shelter)); }}
                      className="inline-flex items-center gap-1 hover:text-primary transition-colors whitespace-nowrap"
                    >
                      <Phone className="w-2.5 h-2.5 flex-shrink-0" />
                      <span>{formatPhoneDisplay(shelter.phone.split(';')[0])}</span>
                    </a>
                  )}
                  {shelter.email && shelter.phone && (
                    <span className="text-muted-foreground/40" aria-hidden>·</span>
                  )}
                  {shelter.email && (
                    <a
                      href={`mailto:${shelter.email}`}
                      onClick={(e) => { e.stopPropagation(); trackEvent("Email", shelterFields(shelter)); }}
                      className="inline-flex items-center gap-1 hover:text-primary transition-colors min-w-0"
                    >
                      <Mail className="w-2.5 h-2.5 flex-shrink-0" />
                      <span className="truncate">{shelter.email}</span>
                    </a>
                  )}
                </div>
              )}



              <div className="flex items-start justify-between gap-2 mt-2">
                <div className="flex flex-wrap gap-1.5 flex-1 min-w-0">
                  {shelter.dog_walking_available === true && (
                    <span className="inline-flex items-center gap-1 bg-primary/[0.06] text-primary ring-1 ring-inset ring-primary/15 text-[10.5px] font-semibold px-2 py-0.5 rounded-full">
                      <Heart className="w-2.5 h-2.5" />
                      Spacery
                    </span>
                  )}
                  {shelter.volunteering_available === true && (
                    <span className="inline-flex items-center gap-1 bg-primary/[0.06] text-primary ring-1 ring-inset ring-primary/15 text-[10.5px] font-semibold px-2 py-0.5 rounded-full">
                      <Users className="w-2.5 h-2.5" />
                      Wolontariat
                    </span>
                  )}
                  {shelter.dogs_available === true && (
                    <span className="inline-flex items-center gap-1 bg-primary/[0.06] text-primary ring-1 ring-inset ring-primary/15 text-[10.5px] font-semibold px-2 py-0.5 rounded-full">
                      <Dog className="w-2.5 h-2.5" />
                      Psy
                    </span>
                  )}
                  {shelter.cats_available === true && (
                    <span className="inline-flex items-center gap-1 bg-primary/[0.06] text-primary ring-1 ring-inset ring-primary/15 text-[10.5px] font-semibold px-2 py-0.5 rounded-full">
                      <Cat className="w-2.5 h-2.5" />
                      Koty
                    </span>
                  )}
                </div>
                {shelter.last_updated && (
                  <p className="hidden sm:flex text-[11px] text-muted-foreground/70 items-center gap-1 shrink-0 whitespace-nowrap pt-1">
                    <Clock className="w-2.5 h-2.5 shrink-0" />
                    <span>Aktualizacja {getLastUpdatedText(shelter.last_updated)}</span>
                  </p>
                )}
              </div>

              <ShelterCardSections shelter={shelter} sections={getSectionsFor(sectionsMap, shelter.id)} />

              {/* Mobile-only: last updated under section tiles, aligned right */}
              {shelter.last_updated && (
                <p className="flex sm:hidden justify-end text-[9px] text-muted-foreground/70 items-center gap-1 mt-1.5">
                  <Clock className="w-2 h-2 shrink-0" />
                  <span>Aktualizacja {getLastUpdatedText(shelter.last_updated)}</span>
                </p>
              )}


              <div className="mt-3 pt-3 border-t border-border/60">
                <span className="inline-flex items-center gap-1 text-[10.5px] sm:text-[11px] font-semibold text-primary group-hover:gap-1.5 transition-all">
                  Zobacz profil schroniska
                  <ArrowRight className="w-3 h-3 transition-transform duration-300 group-hover:translate-x-0.5" />
                </span>
              </div>
            </div>
          </div>
        </div>
      ))}

      {/* Pagination - on mobile and tablet */}
      {isTabletOrMobile && totalPages > 1 && (
        <div className="flex items-center justify-between gap-2 pt-3 mt-1 border-t border-border/60">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
            disabled={currentPage === 1}
            className="h-8 px-2 gap-1 text-xs"
          >
            <ChevronLeft className="w-4 h-4" />
            <span className="hidden xs:inline">Poprzednia</span>
          </Button>
          
          <span className="text-xs text-muted-foreground tabular-nums">
            {currentPage} / {totalPages}
          </span>
          
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
            disabled={currentPage === totalPages}
            className="h-8 px-2 gap-1 text-xs"
          >
            <span className="hidden xs:inline">Następna</span>
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
      )}

      {/* Scroll to top - only on desktop when there are many shelters */}
      {!isTabletOrMobile && shelters.length > 4 && (
        <div className="pt-4 border-t border-border">
          <Button
            variant="outline"
            size="sm"
            onClick={scrollToTop}
            className="w-full gap-2"
          >
            <ArrowUp className="w-4 h-4" />
            Przewiń do góry
          </Button>
        </div>
      )}
    </div>
  );
};
